import React, { useEffect, useState } from 'react';
import { format, formatDistance } from 'date-fns';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import Page from '../../components/Page';
import Pagination from '../../components/Pagination';
import client from '../../lib/feathers';

export default function AvaConversations() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;
  const [page, setPage] = useState(Number(query.get('page')) || 0);
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const getConversations = async () => {
    try {
      setLoading(true);
      const service = client.service('admin/ava-conversations');
      const result = await service.find({
        query: {
          $limit: limit,
          $skip: page * limit,
        },
      });
      setConversations(result.data);
      setTotal(result.total);
    } catch (e) {
      console.log('error', e);
      alert('There was an issue loading the conversations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getConversations();
  }, [page]);

  const navigateToPage = (p = 0) => {
    if (p * limit > total || p * limit < 0) return;
    navigate(`/admin/ava-conversations?page=${p}`);
    setPage(p);
  };

  const truncate = (str, len = 60) => {
    if (!str) return '—';
    return str.length > len ? str.substring(0, len) + '…' : str;
  };

  return (
    <Page>
      <Breadcrumb elements={[{ name: 'Ava Conversations' }]} />
      <div className="mt-4">
        <h1 className="text-2xl font-semibold text-gray-900">Ava Conversations</h1>
        <p className="mt-1 text-sm text-gray-500">Review all Ava chat sessions across users.</p>
      </div>

      <div className="mt-6 flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Message</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Messages</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens In</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens Out</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                    <th scope="col" className="relative px-6 py-3"><span className="sr-only">View</span></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">Loading...</td>
                    </tr>
                  )}
                  {!loading && conversations.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No conversations found.</td>
                    </tr>
                  )}
                  {!loading && conversations.map((conv) => (
                    <tr key={conv.sessionId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {conv.firstName} {conv.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{conv.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">{truncate(conv.firstMessage)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{conv.messageCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Number(conv.totalTokensIn).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Number(conv.totalTokensOut).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDistance(new Date(conv.lastMessageAt), new Date(), { addSuffix: true })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/admin/ava-conversations/${conv.sessionId}`} className="text-indigo-600 hover:text-indigo-900">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > limit && (
                <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
              )}
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}
